/**
 * Iframe helper functions for report rendering
 */

/**
 * Called when iframe content has loaded
 * Injects mobile CSS and scroll reporter script
 */
export const onReportContentLoad = (iframe, iframeId = null) => {
  if (!iframe) {
    return;
  }
  const reportIframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!reportIframeDoc) {
    return;
  }

  // Get iframe ID from the iframe element itself if not provided
  if (!iframeId) {
    iframeId = iframe.id;
  }

  // Inject bulletproof mobile CSS
  const mobileCSS = reportIframeDoc.createElement('style');
  mobileCSS.textContent = `
    @media (max-width: 768px) {
      body { zoom: 0.4 !important; }
    }
    @media (max-width: 480px) {
      body { zoom: 0.3 !important; }
    }
  `;
  reportIframeDoc.head.appendChild(mobileCSS);

  const reportIframeBody = reportIframeDoc.body;
  Object.assign(reportIframeBody.style, {
    margin: '0',
    padding: '0',
    overflowY: 'scroll',
    height: '100%',
    width: '100%',
    display: 'block'
  });

  // Inject scroll reporter for shadow effect
  // Check if scroll script already exists (prevent re-injection and overwriting saved positions)
  const existingScript = reportIframeDoc.querySelector(`script[data-scroll-reporter="${iframeId}"]`);
  if (existingScript) {
    return; // Skip re-injection to prevent scrollY: 0 overwrite
  }

  try {
    const scrollScript = reportIframeDoc.createElement('script');
    scrollScript.dataset.scrollReporter = iframeId; // Mark script to prevent re-injection
    scrollScript.textContent = `
      window.addEventListener('scroll', function() {
        window.parent.postMessage({
          type: 'scroll-position',
          iframeId: '${iframeId}',
          scrollY: window.scrollY || document.documentElement.scrollTop
        }, '*');
      });
    `;
    reportIframeDoc.head.appendChild(scrollScript);
  } catch (error) {
    console.error("Couldn't inject scroll reporter:", error);
  }
};

/**
 * Sets report content in an iframe using a Blob URL
 * Automatically revokes the blob URL after content loads to prevent memory leaks
 */
export const setReportInIframe = (reportContent, iframeId, onLoadCallback = null) => {
  if (!reportContent) return;
  const reportBlob = new Blob([reportContent], { type: 'text/html' });
  const reportUrl = URL.createObjectURL(reportBlob);
  const reportIframe = document.getElementById(iframeId);
  if (reportIframe) {
    reportIframe.src = reportUrl;
    reportIframe.onload = () => {
      onReportContentLoad(reportIframe, iframeId);

      // Revoke blob URL to prevent memory leak
      // The iframe has loaded the content, so the URL is no longer needed
      URL.revokeObjectURL(reportUrl);

      // Call optional callback after content loads (e.g., for scroll restoration)
      if (onLoadCallback) {
        onLoadCallback(iframeId);
      }
    };
  }
};
